/**
 * 🧪 ULTRA SIMPLE VERIFICATION TEST
 * Absolutely guaranteed working demo with embedded SVG images
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Badge
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Schedule,
  Visibility,
  Camera,
  Person,
  Description,
  VerifiedUser
} from '@mui/icons-material';

// Embedded SVG images (guaranteed to work)
const passportFrontImage = 'data:image/svg+xml;base64,' + btoa(`
  <svg width="400" height="300" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="passportGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" style="stop-color:#2196F3;stop-opacity:1" />
        <stop offset="100%" style="stop-color:#1565C0;stop-opacity:1" />
      </linearGradient>
    </defs>
    <rect width="400" height="300" fill="url(#passportGrad)"/>
    <rect x="10" y="10" width="380" height="280" fill="none" stroke="white" stroke-width="3"/>
    <rect x="20" y="20" width="360" height="50" fill="rgba(255,255,255,0.1)"/>
    <text x="200" y="50" text-anchor="middle" fill="white" font-size="20" font-weight="bold">PASSPORT</text>
    <rect x="20" y="80" width="120" height="140" fill="rgba(255,255,255,0.2)" stroke="white"/>
    <text x="80" y="160" text-anchor="middle" fill="white" font-size="12">PHOTO</text>
    <text x="200" y="120" fill="white" font-size="16" font-weight="bold">JOHN DOE</text>
    <text x="200" y="145" fill="white" font-size="12">PASSPORT NO: A12345678</text>
    <text x="200" y="165" fill="white" font-size="12">NATIONALITY: NIGERIA</text>
    <text x="200" y="185" fill="white" font-size="12">EXPIRY: 31 DEC 2030</text>
    <rect x="20" y="230" width="360" height="50" fill="rgba(0,0,0,0.3)"/>
    <text x="200" y="255" text-anchor="middle" fill="white" font-size="14" font-family="monospace">P&lt;NGA&lt;&lt;DOE&lt;&lt;JOHN&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;&lt;</text>
    <text x="200" y="270" text-anchor="middle" fill="white" font-size="14" font-family="monospace">A123456789NGA8501011M2512314&lt;&lt;&lt;&lt;&lt;</text>
  </svg>
`);

const selfiePhotoImage = 'data:image/svg+xml;base64,' + btoa(`
  <svg width="400" height="300" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <radialGradient id="selfieGrad" cx="50%" cy="40%" r="60%">
        <stop offset="0%" style="stop-color:#4CAF50;stop-opacity:1" />
        <stop offset="100%" style="stop-color:#2E7D32;stop-opacity:1" />
      </radialGradient>
    </defs>
    <rect width="400" height="300" fill="url(#selfieGrad)"/>
    <circle cx="200" cy="120" r="80" fill="rgba(255,255,255,0.2)" stroke="white" stroke-width="3"/>
    <circle cx="200" cy="120" r="50" fill="rgba(255,255,255,0.1)"/>
    <circle cx="185" cy="105" r="8" fill="white"/>
    <circle cx="215" cy="105" r="8" fill="white"/>
    <path d="M 170 140 Q 200 160 230 140" stroke="white" stroke-width="3" fill="none"/>
    <text x="200" y="230" text-anchor="middle" fill="white" font-size="18" font-weight="bold">SELFIE PHOTO</text>
    <text x="200" y="260" text-anchor="middle" fill="white" font-size="14">IDENTITY VERIFICATION</text>
  </svg>
`);

const nationalIdImage = 'data:image/svg+xml;base64,' + btoa(`
  <svg width="400" height="300" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="idGrad" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" style="stop-color:#FF9800;stop-opacity:1" />
        <stop offset="100%" style="stop-color:#F57C00;stop-opacity:1" />
      </linearGradient>
    </defs>
    <rect width="400" height="300" fill="url(#idGrad)"/>
    <rect x="15" y="15" width="370" height="270" fill="none" stroke="white" stroke-width="2"/>
    <text x="200" y="45" text-anchor="middle" fill="white" font-size="18" font-weight="bold">NATIONAL IDENTITY CARD</text>
    <rect x="30" y="60" width="100" height="120" fill="rgba(255,255,255,0.2)" stroke="white"/>
    <text x="80" y="130" text-anchor="middle" fill="white" font-size="10">PHOTO</text>
    <text x="200" y="90" fill="white" font-size="16" font-weight="bold">JANE SMITH</text>
    <text x="200" y="115" fill="white" font-size="12">ID NO: 12345678901</text>
    <text x="200" y="135" fill="white" font-size="12">DOB: 15 JAN 1990</text>
    <text x="200" y="155" fill="white" font-size="12">GENDER: FEMALE</text>
    <text x="200" y="175" fill="white" font-size="12">STATE: LAGOS</text>
    <rect x="30" y="220" width="340" height="30" fill="rgba(0,0,0,0.2)"/>
    <text x="200" y="240" text-anchor="middle" fill="white" font-size="12">FEDERAL REPUBLIC OF NIGERIA</text>
  </svg>
`);

const selfiePhoto2Image = 'data:image/svg+xml;base64,' + btoa(`
  <svg width="400" height="300" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <radialGradient id="selfie2Grad" cx="50%" cy="40%" r="60%">
        <stop offset="0%" style="stop-color:#9C27B0;stop-opacity:1" />
        <stop offset="100%" style="stop-color:#6A1B9A;stop-opacity:1" />
      </radialGradient>
    </defs>
    <rect width="400" height="300" fill="url(#selfie2Grad)"/>
    <circle cx="200" cy="120" r="80" fill="rgba(255,255,255,0.2)" stroke="white" stroke-width="3"/>
    <circle cx="200" cy="120" r="50" fill="rgba(255,255,255,0.1)"/>
    <circle cx="185" cy="105" r="8" fill="white"/>
    <circle cx="215" cy="105" r="8" fill="white"/>
    <path d="M 170 140 Q 200 160 230 140" stroke="white" stroke-width="3" fill="none"/>
    <text x="200" y="230" text-anchor="middle" fill="white" font-size="18" font-weight="bold">SELFIE PHOTO</text>
    <text x="200" y="260" text-anchor="middle" fill="white" font-size="14">IDENTITY VERIFICATION</text>
  </svg>
`);

// Mock data with embedded images
const mockRequests = [
  {
    verificationId: 'UPLOAD_1234567890_abc123',
    userId: 'user-001',
    userName: 'John Doe',
    userEmail: 'john@example.com',
    userPhone: '+2348012345678',
    documentType: 'passport',
    status: 'pending_review' as const,
    submittedAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    imageUrls: [passportFrontImage, selfiePhotoImage],
    documentData: {
      fullName: 'John Doe',
      passportNumber: 'A12345678',
      expiryDate: '2030-12-31'
    }
  },
  {
    verificationId: 'UPLOAD_2345678901_def456',
    userId: 'user-002', 
    userName: 'Jane Smith',
    userEmail: 'jane@example.com',
    documentType: 'national_id',
    status: 'pending_review' as const,
    submittedAt: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
    imageUrls: [nationalIdImage, selfiePhoto2Image]
  }
];

const UltraSimpleVerificationTest: React.FC = () => {
  const [selectedRequest, setSelectedRequest] = useState<any>(null);

  const getStatusColor = (status: string) => {
    const colors = {
      pending_review: 'warning',
      verified: 'success',
      rejected: 'error'
    } as const;
    return colors[status as keyof typeof colors] || 'default';
  };

  const getStatusIcon = (status: string) => {
    const icons = {
      pending_review: <Schedule />,
      verified: <CheckCircle />,
      rejected: <Cancel />
    };
    return icons[status as keyof typeof icons];
  };

  const getDocumentTypeIcon = (docType: string) => {
    const icons = {
      passport: <Description />,
      national_id: <Person />,
      driver_license: <VerifiedUser />,
      selfie: <Camera />
    };
    return icons[docType as keyof typeof icons] || <Description />;
  };

  const stats = {
    total: mockRequests.length,
    pending: mockRequests.filter(r => r.status === 'pending_review').length,
    verified: mockRequests.filter(r => r.status === 'verified').length,
    rejected: mockRequests.filter(r => r.status === 'rejected').length
  };

  return (
    <Box p={3}>
      <Alert severity="success" sx={{ mb: 3 }}>
        <Typography variant="h6" component="div" gutterBottom>
          ✅ Ultra Simple Admin Verification - GUARANTEED WORKING IMAGES!
        </Typography>
        <Typography variant="body2">
          This version uses embedded SVG images that are guaranteed to display. 
          Click "View" to see realistic document images with detailed content.
        </Typography>
      </Alert>

      {/* Statistics Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="h6">
                    Total Requests
                  </Typography>
                  <Typography variant="h4">
                    {stats.total}
                  </Typography>
                </Box>
                <VerifiedUser color="primary" sx={{ fontSize: 40, opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="h6">
                    Pending Review
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {stats.pending}
                  </Typography>
                </Box>
                <Schedule color="warning" sx={{ fontSize: 40, opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="h6">
                    Verified
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {stats.verified}
                  </Typography>
                </Box>
                <CheckCircle color="success" sx={{ fontSize: 40, opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="h6">
                    Rejected
                  </Typography>
                  <Typography variant="h4" color="error.main">
                    {stats.rejected}
                  </Typography>
                </Box>
                <Cancel color="error" sx={{ fontSize: 40, opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Verification Requests Table */}
      <Paper elevation={2}>
        <Box p={2}>
          <Typography variant="h6" gutterBottom>
            Verification Requests - Ultra Reliable Demo
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Document Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Submitted</TableCell>
                  <TableCell>Documents</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {mockRequests.map((request) => (
                  <TableRow key={request.verificationId}>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <Avatar sx={{ mr: 2, width: 32, height: 32 }}>
                          {request.userName.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {request.userName}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {request.userEmail}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        {getDocumentTypeIcon(request.documentType)}
                        <Typography variant="body2" sx={{ ml: 1 }}>
                          {request.documentType.replace('_', ' ').toUpperCase()}
                        </Typography>
                      </Box>
                    </TableCell>
                    
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(request.status)}
                        label={request.status.replace('_', ' ').toUpperCase()}
                        color={getStatusColor(request.status)}
                        size="small"
                      />
                    </TableCell>
                    
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(request.submittedAt).toLocaleDateString()}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {new Date(request.submittedAt).toLocaleTimeString()}
                      </Typography>
                    </TableCell>
                    
                    <TableCell>
                      <Badge badgeContent={request.imageUrls.length} color="primary">
                        <Camera fontSize="small" />
                      </Badge>
                    </TableCell>
                    
                    <TableCell>
                      <Button 
                        size="small" 
                        variant="contained"
                        startIcon={<Visibility />}
                        onClick={() => setSelectedRequest(request)}
                        sx={{ mr: 1 }}
                      >
                        View Documents
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Paper>

      {/* Full Document Viewer */}
      {selectedRequest && (
        <Paper elevation={3} sx={{ mt: 3, p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5">
              📄 {selectedRequest.userName} - Document Verification
            </Typography>
            <Button 
              variant="outlined" 
              onClick={() => setSelectedRequest(null)}
            >
              Close Viewer
            </Button>
          </Box>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom>
                User Information
              </Typography>
              <Box mb={2}>
                <Typography variant="body2" color="textSecondary">Name</Typography>
                <Typography variant="body1" fontWeight="bold">{selectedRequest.userName}</Typography>
              </Box>
              <Box mb={2}>
                <Typography variant="body2" color="textSecondary">Email</Typography>
                <Typography variant="body1">{selectedRequest.userEmail}</Typography>
              </Box>
              {selectedRequest.userPhone && (
                <Box mb={2}>
                  <Typography variant="body2" color="textSecondary">Phone</Typography>
                  <Typography variant="body1">{selectedRequest.userPhone}</Typography>
                </Box>
              )}
              <Box mb={2}>
                <Typography variant="body2" color="textSecondary">Document Type</Typography>
                <Typography variant="body1">{selectedRequest.documentType.replace('_', ' ').toUpperCase()}</Typography>
              </Box>
              <Box mb={2}>
                <Typography variant="body2" color="textSecondary">Submitted</Typography>
                <Typography variant="body1">{new Date(selectedRequest.submittedAt).toLocaleString()}</Typography>
              </Box>
              
              {selectedRequest.documentData && (
                <Box mb={3}>
                  <Typography variant="body2" color="textSecondary" gutterBottom>Extracted Data</Typography>
                  <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
                    <pre style={{ fontSize: '0.8rem', margin: 0, whiteSpace: 'pre-wrap' }}>
                      {JSON.stringify(selectedRequest.documentData, null, 2)}
                    </pre>
                  </Box>
                </Box>
              )}
              
              <Box display="flex" gap={1}>
                <Button 
                  variant="contained" 
                  color="success" 
                  startIcon={<CheckCircle />}
                >
                  Approve
                </Button>
                <Button 
                  variant="contained" 
                  color="error" 
                  startIcon={<Cancel />}
                >
                  Reject
                </Button>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={8}>
              <Typography variant="h6" gutterBottom>
                Document Images ({selectedRequest.imageUrls.length})
              </Typography>
              <Grid container spacing={2}>
                {selectedRequest.imageUrls.map((imageUrl: string, index: number) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Card elevation={2}>
                      <Box
                        component="img"
                        src={imageUrl}
                        alt={`Document ${index + 1}`}
                        sx={{
                          width: '100%',
                          height: 250,
                          objectFit: 'contain',
                          bgcolor: 'grey.50'
                        }}
                      />
                      <CardContent>
                        <Typography variant="caption" color="textSecondary">
                          Document {index + 1} - {index === 0 ? 'Primary Document' : 'Selfie Verification'}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Paper>
      )}
    </Box>
  );
};

export default UltraSimpleVerificationTest;